import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Sparkles, Timer, Trophy, X } from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { useMundialPredictions } from "@/hooks/useMundialPredictions";
import {
  badgeForXp,
  remainingSeconds,
  MUNDIAL_EVENT_LABELS,
  type MundialPredictionEvent,
} from "@/lib/predictions/mundialEngine";
import {
  submitMundialAnswer,
  settleMundialAnswer,
} from "@/lib/predictions/mundialPredictions.functions";
import { trackEvent } from "@/lib/audit";
import { setLayerActive, useLayerOffset } from "@/lib/pr30/zeroOverlapPolicy";

// RFC 4122 UUID matcher. The Mundial Prediction Engine persists events in
// Supabase with uuid PKs, but the demo fallback (MOCK_PREDICTION_EVENTS)
// surfaces synthetic ids like `pe-1`. Those must NOT be sent to the server
// fn — its Zod validator rejects them with "Invalid uuid".
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Mundial Prediction Engine™ — elegant floating card.
 *
 * Sits at the bottom-right corner. Non-modal, always dismissable. Lists the
 * active micro-prediction events for the current match with a 30/60/90 sec
 * countdown, the available options, and the user's accumulated XP + badge.
 */
export function MundialPredictionFloatingCard({ matchId }: { matchId: string | undefined }) {
  const { events, answers, totalXp, refresh } = useMundialPredictions(matchId);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const submit = useServerFn(submitMundialAnswer);
  const settle = useServerFn(settleMundialAnswer);
  // P0-MOBILE-OVERLAY-01 — participate in PR-30 Zero Overlap Policy so we
  // stack above BottomNav + MobileShareDock and so RetentionFab can push
  // itself further up when this card is on screen.
  const stackOffset = useLayerOffset("fab_secondary");

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const cardVisible =
    !dismissed && !!matchId && (events.some((e) => e.status === "open" || e.status === "resolved"));
  useEffect(() => {
    setLayerActive("fab_secondary", cardVisible);
    return () => setLayerActive("fab_secondary", false);
  }, [cardVisible]);

  const openEvents = useMemo(
    () => events.filter((e) => e.status === "open" && remainingSeconds(e.end_time, now) > 0),
    [events, now],
  );
  const resolvedEvents = useMemo(
    () => events.filter((e) => e.status === "resolved"),
    [events],
  );

  // Auto-settle resolved events the user answered.
  useEffect(() => {
    for (const ev of resolvedEvents) {
      const a = answers[ev.id];
      if (a && a.is_correct === null) {
        if (!UUID_RE.test(ev.id)) continue; // demo event — no server row
        void settle({ data: { predictionEventId: ev.id } }).then((res) => {
          if (res?.settled && res.points > 0) {
            toast.success(`+${res.points} XP`, { description: `${ev.question} ✅` });
          }
          void refresh();
        }).catch(() => { /* silent */ });
      }
    }
  }, [resolvedEvents, answers, settle, refresh]);

  if (dismissed) return null;
  if (!matchId) return null;
  if (openEvents.length === 0 && resolvedEvents.length === 0) return null;

  // Registered effect must run regardless of early-return order — we wrap
  // it below so it only fires when the card is actually mounted-visible.

  const { current: badge, next: nextBadge, progress } = badgeForXp(totalXp);

  const onVote = async (ev: MundialPredictionEvent, option: string) => {
    if (answers[ev.id]) return;
    try {
      if (UUID_RE.test(ev.id)) {
        await submit({ data: { predictionEventId: ev.id, selectedAnswer: option } });
      }
      // Demo events (non-UUID ids) skip the server round-trip: there is no
      // corresponding prediction_events row, so the serverFn would 400.
      void trackEvent("prediction_vote" as never, {
        surface: "mundial_engine",
        event_type: ev.event_type,
        window: ev.window_seconds,
        match_id: matchId,
      });
      toast("Predicción registrada", { description: `${MUNDIAL_EVENT_LABELS[ev.event_type]} · ${option}` });
      await refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Error";
      toast.error("No se pudo registrar", { description: message });
    }
  };

  return (
    <div
      className="fixed z-[55] right-3 w-[min(360px,calc(100vw-1.5rem))]"
      style={{
        bottom: `calc(env(safe-area-inset-bottom, 0px) + ${(stackOffset || 0) + 76}px)`,
      }}
      role="region"
      aria-label="Mundial Prediction Engine"
    >
      <div className="overflow-hidden rounded-2xl border border-border/60 bg-background/95 shadow-2xl backdrop-blur-xl">
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-border/40 bg-gradient-to-r from-primary/15 via-accent/10 to-primary/15 px-3 py-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/20">
            <Sparkles className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[9px] font-black uppercase tracking-[0.16em] text-primary">
              Mundial Prediction
            </div>
            <div className="truncate text-[12px] font-bold leading-tight">
              {badge.emoji} {badge.label} · {totalXp} XP
            </div>
          </div>
          <button
            type="button"
            aria-label={collapsed ? "Expandir" : "Colapsar"}
            onClick={() => setCollapsed((v) => !v)}
            className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-accent"
          >
            {collapsed ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={() => setDismissed(true)}
            className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-accent"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Badge progress */}
        {!collapsed && nextBadge && (
          <div className="px-3 pt-2">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary to-primary/70 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Trophy className="h-3 w-3" /> próximo: {nextBadge.label}</span>
              <span>{nextBadge.minXp - totalXp} XP</span>
            </div>
          </div>
        )}

        {/* Events */}
        {!collapsed && (
          <div className="max-h-[52vh] space-y-2 overflow-y-auto p-3">
            {openEvents.length === 0 && (
              <p className="text-center text-[11px] text-muted-foreground py-4">
                Esperando próxima predicción…
              </p>
            )}
            {openEvents.map((ev) => {
              const left = remainingSeconds(ev.end_time, now);
              const answer = answers[ev.id];
              const opts = [
                { id: ev.option_a, label: ev.option_a },
                { id: ev.option_b, label: ev.option_b },
                ...(ev.option_c ? [{ id: ev.option_c, label: ev.option_c }] : []),
              ];
              return (
                <article
                  key={ev.id}
                  className="rounded-xl border border-border/50 bg-background/70 p-2.5"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-[12px] font-bold leading-tight">
                      {ev.question}
                    </h3>
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-black text-primary">
                      +{ev.points_reward}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-[10px] font-bold text-muted-foreground">
                    <Timer className="h-3 w-3" />
                    <span>{left}s · {MUNDIAL_EVENT_LABELS[ev.event_type]}</span>
                  </div>
                  <div className={`mt-2 grid gap-1.5 ${opts.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
                    {opts.map((o) => {
                      const picked = answer?.selected_answer === o.id;
                      return (
                        <button
                          key={o.id}
                          type="button"
                          disabled={Boolean(answer)}
                          onClick={() => void onVote(ev, o.id)}
                          className={`rounded-lg px-2 py-1.5 text-[11px] font-black transition active:scale-95 ${
                            picked
                              ? "bg-primary text-primary-foreground shadow"
                              : answer
                                ? "bg-secondary text-muted-foreground opacity-60"
                                : "bg-secondary text-foreground hover:bg-accent"
                          }`}
                        >
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                </article>
              );
            })}

            {resolvedEvents.length > 0 && (
              <div className="pt-1">
                <div className="mb-1 text-[9px] font-black uppercase tracking-wider text-muted-foreground">
                  Resueltas
                </div>
                <div className="space-y-1">
                  {resolvedEvents.slice(-3).map((ev) => {
                    const a = answers[ev.id];
                    const correct = a?.is_correct;
                    return (
                      <div
                        key={ev.id}
                        className="flex items-center justify-between rounded-lg border border-border/40 bg-background/60 px-2 py-1.5 text-[11px]"
                      >
                        <span className="min-w-0 truncate">{ev.question}</span>
                        <span className={`ml-2 shrink-0 font-black ${correct ? "text-emerald-500" : "text-muted-foreground"}`}>
                          {correct ? `+${a?.points_awarded ?? ev.points_reward}` : a ? "—" : ev.correct_answer ?? ""}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}